import React from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { ArrowLeft, Calendar, TrendingUp, AlertTriangle } from 'lucide-react';
import BalanceExplanation from '@/components/ferie/BalanceExplanation';

const STATO_COL = {
  richiesta: 'bg-amber-100', approvata: 'bg-emerald-100', programmata: 'bg-blue-100',
  goduta: 'bg-stone-300', rifiutata: 'bg-red-100', annullata: 'bg-stone-100',
};

const MESI = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
const roundDays = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.round(numeric * 100) / 100 : 0;
};
const fmtDays = (value) => roundDays(value).toLocaleString('it-IT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const signedDays = (value) => {
  const rounded = roundDays(value);
  const negative = rounded < 0 || Object.is(rounded, -0);
  return `${negative ? '−' : '+'}${fmtDays(Math.abs(rounded))}`;
};
const fmtDate = (value) => {
  if (!value) return 'n/d';
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('it-IT');
};

export default function FerieDipendente() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { dipendenti, configOf, saldoOf, storicoOf, periodi, ANNO_CORRENTE, hydrated, hydrationError } = useApp();
  const d = dipendenti.find(x => x.id === id);
  if (!d) {
    return (
      <div className="p-8 text-center">
        <p>Dipendente non trovato.</p>
        <Link to="/ferie" className="text-brand-green underline">Torna alla lista</Link>
      </div>
    );
  }
  const cfg = configOf(d.id);
  const requestedYear = Number(new URLSearchParams(search).get('anno'));
  const selectedYear = requestedYear >= 2000 && requestedYear <= 2200 ? requestedYear : ANNO_CORRENTE;
  const isHistoricalYear = selectedYear !== ANNO_CORRENTE;
  // Se baseline presente, il motore usa asOfDate >= baseline.data → passiamo 31/12 anno competenza per vedere proiezione
  const asOfDate = cfg?.baseline ? new Date(cfg.annoCompetenza, 11, 31) : new Date();
  const saldo = saldoOf(d.id, asOfDate);
  const storico = storicoOf(d.id);
  const storicoAnno = storico.find(y => y.anno === selectedYear);
  const miei = periodi
    .filter(p => p.dipendenteId === d.id && (p.annoCompetenza || selectedYear) === selectedYear)
    .sort((a,b) => a.dal.localeCompare(b.dal));
  // The server saldo carries the calculated values; the verified baseline
  // metadata is part of the hydrated employee configuration.
  const baseline = cfg?.baseline || saldo?.baselineUsata || null;
  const baselineDate = baseline ? new Date(`${baseline.data}T00:00:00`) : null;
  const baselineMonth = baselineDate && !Number.isNaN(baselineDate.getTime()) ? baselineDate.getMonth() + 1 : null;
  const currentMonth = new Date().getFullYear() === selectedYear ? new Date().getMonth() + 1 : 12;
  if (!saldo) {
    return (
      <div className="rounded-2xl border border-stone-200 bg-white p-8 text-center" data-testid="ferie-dipendente-loading">
        <div className="font-display font-semibold text-stone-900">
          {hydrationError ? 'Saldo ferie non disponibile' : 'Caricamento saldo ferie…'}
        </div>
        <p className="mt-2 text-sm text-stone-500">
          {hydrationError || (!hydrated ? 'Attendo i dati aggiornati del dipendente.' : 'Nessun saldo disponibile per questo dipendente.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button data-testid="btn-back" onClick={() => navigate(-1)} className="text-sm text-stone-500 hover:text-stone-900 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4"/> Torna
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-brand-green-hover font-display font-semibold text-2xl flex items-center justify-center">
          {d.nome[0]}{d.cognome[0]}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-stone-900">{d.displayName || `${d.nome} ${d.cognome}`}</h1>
          <div className="text-sm text-stone-500">{d.reparto}{d.email ? ` · ${d.email}` : ''}</div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-stone-100 text-stone-700 px-2.5 py-1">{cfg.tipoContratto || 'Contratto n/d'}</span>
            <span className="rounded-full bg-emerald-50 text-brand-green px-2.5 py-1 font-semibold">{cfg.ferieAnnue}g/anno</span>
            {cfg.override && <span className="rounded-full bg-amber-50 text-amber-900 px-2.5 py-1 font-semibold">Override</span>}
            <span className="rounded-full bg-blue-50 text-blue-800 px-2.5 py-1">Inizio: {cfg.dataInizio || 'n/d'}</span>
            {cfg.dataFine && <span className="rounded-full bg-red-50 text-red-700 px-2.5 py-1">Fine: {cfg.dataFine}</span>}
          </div>
        </div>
      </div>

      <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green">Situazione ferie {selectedYear}</div>

      {isHistoricalYear ? (
        <section className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="storico-anno-dettaglio">
          <div className="font-display font-semibold text-stone-900 tracking-tight">Storico ferie {selectedYear}</div>
          <p className="mt-1 text-sm text-stone-500">Valore storico registrato per l’anno selezionato.</p>
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-3xl font-display font-semibold text-amber-900">
              {storicoAnno ? fmtDays(storicoAnno.residuo) : '—'}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-amber-800 font-bold">Residuo storico</div>
            {!storicoAnno && <div className="mt-2 text-xs text-amber-800">Nessun saldo storico disponibile per questo anno.</div>}
          </div>
        </section>
      ) : (
        <>
          <section className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="saldo-riepilogo">
            <div className="mb-4">
              <div className="font-display font-semibold text-stone-900 tracking-tight">Saldo anno {saldo.annoCompetenza} · 01/01 → 31/12</div>
              <div className="text-xs text-stone-500">
                Il saldo e la spiegazione seguono i valori restituiti dal server per il dipendente e l&apos;anno selezionato.
              </div>
            </div>
            <div className="mt-3 text-xs text-stone-500 flex flex-wrap gap-3">
              <span>Data inizio lavoro: <b className="text-stone-800">{cfg.dataInizio || 'Da completare'}</b></span>
              <span>Data fine rapporto: <b className="text-stone-800">{cfg.dataFine || 'n/d'}</b></span>
            </div>
          </section>

          <BalanceExplanation
            balance={saldo}
            baseline={baseline}
            year={saldo.annoCompetenza}
            projection={Boolean(baseline)}
            policy={saldo.policy}
            testid="saldo-formula"
          />

          {/* Maturazione mese per mese */}
          <section className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="maturazione-mensile">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-brand-green"/>
              <div>
                <div className="font-display font-semibold text-stone-900 tracking-tight">Maturazione mese per mese</div>
                <div className="mt-1 text-xs text-stone-500">Mostra il maturato di riferimento e la maturazione prevista fino a fine anno.</div>
              </div>
            </div>
            {baseline && (
              <div className="mt-3 rounded-xl bg-stone-50 border border-stone-200 px-3 py-2 text-xs text-stone-700">
                <b>Coperto dalla baseline</b>: fino al {fmtDate(baseline.data)} ({fmtDays(baseline.saldo)} giorni) — non ricostruito
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-stone-500">
              <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-stone-300" /> Baseline</span>
              <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-emerald-500" /> Maturato di riferimento</span>
              <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-blue-400" /> Previsto</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-12">
              {saldo.mesePerMese.map((m, i) => {
                const prev = i > 0 ? saldo.mesePerMese[i - 1].maturate : 0;
                const delta = roundDays(m.maturate - prev);
                const isBaselineMonth = baselineMonth !== null && m.mese <= baselineMonth;
                const isProjected = !isBaselineMonth && m.mese > currentMonth;
                const state = isBaselineMonth ? 'baseline' : isProjected ? 'prevista' : 'riferimento';
                return (
                  <div key={m.mese} data-testid={`mese-${m.mese}`} data-state={state} className={`rounded-xl border p-2 text-center ${
                    isBaselineMonth
                      ? 'border-stone-200 bg-stone-50/70'
                      : isProjected
                        ? 'border-blue-200 bg-blue-50/60'
                        : 'border-emerald-200 bg-emerald-50/60'
                  }`}>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">{MESI[i]}</div>
                    <div className={`text-sm font-display font-semibold mt-0.5 ${isBaselineMonth ? 'text-stone-300' : 'text-stone-900'}`}>
                      {isBaselineMonth ? '—' : signedDays(delta)}
                    </div>
                    <div className="text-[10px] text-stone-500 font-medium">
                      {isBaselineMonth ? 'in baseline' : isProjected ? 'prevista' : 'di riferimento'}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 space-y-1 text-xs text-stone-500">
              {baseline && <div>Maturazione prevista dopo la baseline: <b className="text-stone-900">+{fmtDays(saldo.maturate)} giorni</b></div>}
              <div>{baseline ? `Saldo previsto al 31/12/${saldo.annoCompetenza}` : 'Saldo alla data di riferimento'}: <b className="text-brand-green">{fmtDays(saldo.disponibile)} giorni</b></div>
            </div>
          </section>
        </>
      )}

      {/* Timeline periodi */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4 text-brand-green"/>
          <div className="font-display font-semibold text-stone-900 tracking-tight">Timeline periodi ferie</div>
        </div>
        {miei.length === 0 ? (
          <div className="text-sm text-stone-500 text-center py-4">Nessun periodo registrato per questa selezione.</div>
        ) : (
          <div className="space-y-2">
            {miei.map(p => (
              <div key={p.id} className="flex items-center gap-3 rounded-xl border border-stone-100 px-3 py-2">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${STATO_COL[p.stato] || 'bg-stone-100'}`}/>
                <div className="text-sm text-stone-900 font-medium min-w-[120px]">{p.dal} → {p.al}</div>
                <div className="text-xs text-stone-500">{p.giorni}g</div>
                <div className="text-xs text-stone-500 flex-1 truncate">{p.note}</div>
                <div className="text-[11px] font-semibold text-stone-700 uppercase">{p.stato}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Storico multi-anno */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5">
        <div className="font-display font-semibold text-stone-900 tracking-tight mb-3">Storico anni precedenti</div>
        {storico.length === 0 ? (
          <div className="text-sm text-stone-500 py-3">Nessuno storico disponibile.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {storico.map(y => (
              <Link
                key={y.anno}
                to={`/ferie/${id}?anno=${y.anno}`}
                data-testid={`storico-${y.anno}`}
                aria-current={selectedYear === y.anno ? 'page' : undefined}
                className={`block rounded-xl border p-3 transition-colors hover:border-brand-green hover:bg-emerald-50/50 ${
                y.residuo > 0 ? 'border-amber-200 bg-amber-50' : 'border-stone-200 bg-stone-50/50'
                }`}
              >
                <div className="text-sm font-display font-semibold text-stone-900">{y.anno}</div>
                <div className="text-xs text-stone-500 mt-0.5">
                  {y.residuo > 0 ? <span className="text-amber-900 font-semibold">Residuo: {fmtDays(y.residuo)} giorni</span> : 'Chiuso / OK'}
                </div>
                <div className="text-[10px] text-stone-400 mt-1 truncate">{y.note}</div>
                <div className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-brand-green">Apri anno</div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {cfg.dataFine && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-700 mt-0.5"/>
          <div className="text-sm text-red-800">
            Rapporto in scadenza il <b>{cfg.dataFine}</b>. La maturazione si ferma a quella data.
          </div>
        </div>
      )}
    </div>
  );
}
